// Embedded membership plans.
//
// The plans render INLINE on the host page — name, price, perks, description —
// and "Join" hands over to the widget's own purchase flow in a modal. The same
// division of labour as the shop: the embed sells the idea, the widget takes
// the money (login, terms, card), so none of that is duplicated here.
//
// Host markup (wired up in widget.ts):
//   <div data-calendoula-memberships></div>            all plans sold online
//   <div data-calendoula-memberships data-plan="3"></div>   just one
//   <button data-calendoula-memberships-button>Join</button> straight to the modal
//   ...also `.membership-btn` on any element, matching `.book-btn`.

import { loadMembershipPlans } from './api';
import type { MembershipPlanPublic } from './types';

const STYLE_ID = 'pp-mem-embed-styles';

const CSS = `
.pp-mem-embed { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; display: grid; gap: 12px; }
.pp-mem-card { border: 1px solid #eadfd6; border-radius: 14px; background: #fff; padding: 16px 18px; display: flex; gap: 16px; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; }
.pp-mem-info { flex: 1 1 220px; min-width: 0; }
.pp-mem-name { font-size: 16px; font-weight: 600; color: #3b322c; }
.pp-mem-desc { font-size: 13.5px; color: #6f645c; margin-top: 4px; }
.pp-mem-perks { font-size: 13px; color: #7b8f6d; margin-top: 6px; }
.pp-mem-with { font-size: 12.5px; color: #8a7f76; margin-top: 4px; }
.pp-mem-right { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; flex: none; }
.pp-mem-price { font-size: 15px; font-weight: 600; color: #3b322c; white-space: nowrap; }
.pp-mem-join { border: none; border-radius: 999px; background: #b8623e; color: #fff; font-size: 13.5px; padding: 8px 18px; cursor: pointer; }
.pp-mem-join:hover { background: #a3552f; }
.pp-mem-empty { font-size: 13.5px; color: #8a7f76; }
`;

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

function esc(str: string): string {
  return String(str ?? '').replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}

function money(n: number): string {
  return `$${Number(n || 0).toFixed(2).replace(/\.00$/, '')}`;
}

/** "$99/mo", "$249 every 3 months" — the same shape the widget's own list uses. */
function billingLabel(plan: MembershipPlanPublic): string {
  const units: Record<string, string> = { day: 'day', week: 'wk', month: 'mo', year: 'yr' };
  const u = units[plan.billingUnit] || plan.billingUnit;
  return plan.billingInterval === 1
    ? `${money(plan.price)}/${u}`
    : `${money(plan.price)} every ${plan.billingInterval} ${u}s`;
}

function perkLabel(type: string | undefined, percent: number | undefined, amount: number | undefined, what: string): string {
  return type === 'amount' ? `${money(amount || 0)} off ${what}` : `${percent || 0}% off ${what}`;
}

export function planPerks(p: MembershipPlanPublic): string[] {
  const perks: string[] = [];
  if (p.discountServices) perks.push(perkLabel(p.discountServicesType, p.discountServicesPercent, p.discountServicesAmount, 'services'));
  if (p.discountEnhancements) perks.push(perkLabel(p.discountEnhancementsType, p.discountEnhancementsPercent, p.discountEnhancementsAmount, 'enhancements'));
  if (p.discountProducts) perks.push(perkLabel(p.discountProductsType, p.discountProductsPercent, p.discountProductsAmount, 'products'));
  return perks;
}

/** "With Kayla or Sarah" — a restricted plan says so before the money moves. */
export function providerNote(p: MembershipPlanPublic): string {
  const names = p.redemptionStaffNames || [];
  if (!names.length) return '';
  if (names.length === 1) return `With ${names[0]}`;
  return `With ${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

export interface MembershipEmbedOptions {
  /** Show only this plan. */
  planId?: number;
  /** Display only — no "Join" button (the page has its own call to action). */
  hideJoin?: boolean;
  /** Label on the join button. */
  joinLabel?: string;
}

/**
 * Render the plans into `el`. `onJoin` opens the widget's purchase flow — the
 * embed never handles money itself.
 */
export async function mountMembershipEmbed(
  el: HTMLElement, apiBase: string,
  onJoin: (planId: number) => void,
  opts: MembershipEmbedOptions = {},
): Promise<void> {
  ensureStyles();
  let plans: MembershipPlanPublic[] = [];
  try {
    plans = await loadMembershipPlans(apiBase);
  } catch {
    // A site that embeds this should not show an error box to its visitors —
    // an empty host reads as "nothing on offer", which is the safe failure.
    el.innerHTML = '';
    return;
  }
  if (opts.planId) plans = plans.filter(p => p.id === opts.planId);
  if (!plans.length) {
    el.innerHTML = `<div class="pp-mem-embed"><p class="pp-mem-empty">No memberships are available online right now.</p></div>`;
    return;
  }
  const label = esc(opts.joinLabel || 'Join');
  el.innerHTML = `<div class="pp-mem-embed">${plans.map(p => {
    const perks = planPerks(p);
    return `
      <div class="pp-mem-card">
        <div class="pp-mem-info">
          <div class="pp-mem-name">${esc(p.name)}</div>
          ${p.description ? `<div class="pp-mem-desc">${esc(p.description)}</div>` : ''}
          ${perks.length ? `<div class="pp-mem-perks">${esc(perks.join(' · '))}</div>` : ''}
          ${providerNote(p) ? `<div class="pp-mem-with">${esc(providerNote(p))}</div>` : ''}
        </div>
        <div class="pp-mem-right">
          <div class="pp-mem-price">${esc(billingLabel(p))}</div>
          ${opts.hideJoin ? '' : `<button type="button" class="pp-mem-join" data-mem-plan="${p.id}">${label}</button>`}
        </div>
      </div>`;
  }).join('')}</div>`;

  el.querySelectorAll<HTMLElement>('[data-mem-plan]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      onJoin(Number(btn.getAttribute('data-mem-plan')));
    });
  });
}
